import { useState } from 'react';
import ReactDOM from 'react-dom';
import PolygonIcon from '../../icons/PolygonIcon';
import EditPerimeterToolbar from './EditPerimeterToolbar';
import { emitCustomEvent } from 'react-custom-events';
import Events from '../../defaults/Events';
import './EditGeometryToolbar.css';
import Fab from '@mui/material/Fab';
import FieldNames from '../../defaults/FieldNames.json';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import EditPointToolbar from './EditPointToolbar';
import { useSelector } from 'react-redux';
import InFormAlert from '../InFormAlert';
import _ from 'lodash';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import EditLineToolbar from './EditLineToolbar';
import PolylineIcon from '@mui/icons-material/Polyline';

export default function EditGeometryToolbar({
    feature,
    isTreatment,
    isActuals,
    perimeter,
    onConfirmPerimeterEdit,
    onDrawComplete,
    handlePerimeterReset,
    onConfirmPointPerimeterEdit,
    onReplacePerimeter,
    onConfirmPointPostFirePerimeterEdit,
    onConfirmLinePostFirePerimeterEdit,
}) {
    const [geometryField, setGeometryField] = useState(null);
    const [confirmDisabled, setConfirmDisabled] = useState(true);
    const [modifiedPerimeter, setModifiedPerimeter] = useState(null);
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const actualTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });
    const [tempFeatureType, setTempFeatureType] = useState(null);
    const [showGeometryConfirmPrompt, setShowGeometryConfirmPrompt] = useState(false);
    const [areUnsavedFields, setAreUnsavedFields] = useState(false);
    const [featureHasFunding, setFeatureHasFunding] = useState(false);
    const isPostFire = isFeaturePostFire(feature?.attributes);
    const geometryPopupWarningText =
        (areUnsavedFields
            ? `You have unsaved changes, if you want to keep those changes ` +
              `you must save before changing geometry. Do you want to continue?`
            : '') +
        (areUnsavedFields && featureHasFunding
            ? `
                                    
                                    `
            : '') +
        (featureHasFunding
            ? `NOTE:  When you make changes to the polygon for a treatment/activity,` +
              `the acres per funding line will be reset to default values (proportions of funding/acres). ` +
              `Please go to the Manage Costs screen review and ensure the proportions make sense for the ` +
              `treatment/activity, and manually update if desired.`
            : '');

    const setGeometryFieldState = (geometryField) => {
        setGeometryField(geometryField);
        setConfirmDisabled(true);
        if (geometryField === 'PERIMETER') {
            if (perimeter === null || perimeter === undefined) {
                emitCustomEvent(Events.EnableDrawMode, { geometry: null }).then((args) => {
                    setConfirmDisabled(false);
                    setModifiedPerimeter(args);
                    emitCustomEvent(Events.ModifiedGraphicChanged, args.geometry);
                });
            } else {
                enablePerimeterDraw();
                setConfirmDisabled(false);
            }
        } else if (geometryField === 'POINT') {
            if (perimeter === null || perimeter === undefined) {
                if (isPostFire) {
                    emitCustomEvent(Events.ToggleIsMovingPoint, false);
                    if (feature) {
                        emitCustomEvent(Events.EnablePostFirePointMode, {
                            geometry: null,
                            callback: postFirePointFeatureChanged,
                            callbackParams: {
                                isPostFire: true,
                            },
                        }).then((args) => {
                            setConfirmDisabled(false);
                            setModifiedPerimeter(args);
                            emitCustomEvent(Events.ModifiedGraphicChanged, args.geometry);
                        });
                    }
                } else {
                    emitCustomEvent(Events.ToggleIsMovingPoint, false);
                    if (feature) {
                        emitCustomEvent(Events.EnablePointMode, {
                            geometry: null,
                            callback: pointFeatureChanged,
                            callbackParams: {
                                isPostFire: false,
                            },
                        }).then((args) => {
                            setConfirmDisabled(false);
                            setModifiedPerimeter(args);
                            emitCustomEvent(Events.ModifiedGraphicChanged, args.geometry);
                        });
                    }
                }
            } else {
                if (isPostFire) {
                    emitCustomEvent(Events.ToggleIsMovingPoint, true);
                    enablePerimeterPointPostFire(perimeter);
                    setConfirmDisabled(false);
                    emitCustomEvent(Events.ModifiedGraphicPostFirePointChanged, {
                        geometry: perimeter,
                    });
                } else {
                    emitCustomEvent(Events.ToggleIsMovingPoint, true);
                    enablePerimeterPoint();
                    setConfirmDisabled(false);
                }
            }
        } else if (geometryField === 'LINE') {
            if (perimeter === null || perimeter === undefined) {
                emitCustomEvent(Events.EnablePostFireLineMode, { geometry: null }).then((args) => {
                    setConfirmDisabled(false);
                    setModifiedPerimeter(args);
                    emitCustomEvent(Events.ModifiedGraphicLineChanged, args.geometry);
                });
            } else {
                enablePerimeterLine();
                setConfirmDisabled(false);
            }
        }
    };

    const cancelPerimeterEdit = () => {
        emitCustomEvent(Events.SetExistingGeo, { geometry: null, attr: null });
        emitCustomEvent(Events.DisableDrawMode);
        setGeometryFieldState(null);
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
    };

    const onClearChangesPerimeter = () => {
        handlePerimeterReset();
    };

    const onUploadPerimeter = (shapefilePerimeter) => {
        let tempFeature;
        if (feature && feature.geometry.type === shapefilePerimeter.type) {
            tempFeature = _.cloneDeep(feature);
            tempFeature.geometry = shapefilePerimeter;
            emitCustomEvent(Events.UploadGeometry, shapefilePerimeter);
        } else {
            throw Error(
                `Expecting ${feature.geometry.type} upload, received ${shapefilePerimeter.type}`
            );
        }
        emitCustomEvent(Events.ModifiedShapeUploaded, tempFeature);
        setModifiedPerimeter(tempFeature);
    };

    const pointFeatureChanged = (graphic) => {
        let newFeature = Object.assign({}, feature);
        if (newFeature) {
            newFeature.geometry = graphic.toJSON();
            newFeature.joinFeature = FieldNames.joinFeatureValues.polygon;
            newFeature.isTreatment = isTreatment;
            newFeature.isPostFire = false;
        }
        setModifiedPerimeter(newFeature);
        emitCustomEvent(Events.EstimatedPointCreated, newFeature);
        if (isTreatment) {
            onDrawComplete('treatment');
        } else {
            onDrawComplete('activity');
        }
    };

    const postFirePointFeatureChanged = (graphic) => {
        let newFeature = Object.assign({}, feature);
        newFeature.geometry = graphic.toJSON();
        newFeature.isTreatment = isTreatment;
        newFeature.isPostFire = true;
        newFeature.joinFeature = newFeature.joinFeature
            ? newFeature.joinFeature
            : FieldNames.joinFeatureValues.point;
        setModifiedPerimeter(newFeature);
        if (newFeature) {
            emitCustomEvent(Events.EstimatedPointCreated, newFeature);
            if (isTreatment) {
                onDrawComplete('treatment');
            } else {
                onDrawComplete('activity');
            }
        }
    };

    const confirmPerimeterEdit = (event) => {
        setGeometryFieldState(null);
        let args = feature;
        if (modifiedPerimeter !== null) {
            args.geometry = modifiedPerimeter.geometry;
        }
        onConfirmPerimeterEdit(args);
    };

    const cancelPointPerimeterEdit = () => {
        emitCustomEvent(Events.SetExistingGeo, { geometry: null, attr: null });
        emitCustomEvent(Events.DisableDrawMode);
        setGeometryFieldState(null);
    };

    const cancelLinePerimeterEdit = () => {
        emitCustomEvent(Events.SetExistingGeo, { geometry: null, attr: null });
        emitCustomEvent(Events.DisableDrawMode);
        setGeometryFieldState(null);
    };

    const confirmPointPerimeterEdit = (event) => {
        setGeometryFieldState(null);
        let args = feature;
        if (modifiedPerimeter !== null) {
            args.geometry = modifiedPerimeter.geometry;
        }
        onConfirmPointPerimeterEdit(args);
    };

    const confirmPointPostFirePerimeterEdit = (event) => {
        setGeometryFieldState(null);
        let args = feature;
        if (modifiedPerimeter !== null) {
            args.geometry = modifiedPerimeter.geometry;
        }
        onConfirmPointPostFirePerimeterEdit(args);
    };

    const confirmLinePostFirePerimeterEdit = (event) => {
        setGeometryFieldState(null);
        let args = feature;
        if (modifiedPerimeter !== null) {
            args.geometry = modifiedPerimeter.geometry;
        }
        onConfirmLinePostFirePerimeterEdit(args);
    };

    const setExistingGeometry = (geo) => {
        if (feature) {
            let geom = geo;
            if ('geometry' in geom) {
                geom = geo.geometry;
            }
            emitCustomEvent(Events.SetExistingGeo, { geometry: geom, attr: feature.attributes });
        }
    };

    const enablePerimeterDraw = () => {
        let tempPerimeter = null;
        if (perimeter) {
            tempPerimeter = perimeter.geometry ? perimeter.geometry : perimeter.perimeter;
        }
        const geo = tempPerimeter;
        emitCustomEvent(Events.ToggleShouldUpdateGraphic, true);
        emitCustomEvent(Events.EnableDrawMode, {
            geometry: geo,
            attributes: feature?.attributes,
            callback: setExistingGeometry,
        });
    };

    const enablePerimeterLine = () => {
        let tempPerimeter = null;
        if (perimeter) {
            tempPerimeter = perimeter.geometry ? perimeter.geometry : perimeter.perimeter;
        }
        const geo = tempPerimeter;
        emitCustomEvent(Events.ToggleShouldUpdateGraphic, true);
        emitCustomEvent(Events.EnablePostFireLineMode, {
            geometry: geo,
            callback: setExistingGeometry,
        });
    };

    const enablePerimeterPoint = () => {
        let tempPerimeter = null;
        if (perimeter) {
            tempPerimeter = perimeter.geometry ? perimeter.geometry : perimeter.perimeter;
        }
        const geo = tempPerimeter;
        emitCustomEvent(Events.EnablePointMode, {
            geometry: geo,
            callback: pointModifiedChanged,
            callbackParams: {
                isPostFire: false,
            },
        });
    };

    const enablePerimeterPointPostFire = (perimeter) => {
        let tempPerimeter = null;
        if (perimeter) {
            tempPerimeter = perimeter.geometry ? perimeter.geometry : perimeter.perimeter;
        }
        const geo = tempPerimeter;
        emitCustomEvent(Events.EnablePostFirePointMode, {
            geometry: geo,
            callback: pointModifiedChanged,
            callbackParams: {
                isPostFire: true,
            },
        });
    };

    const pointModifiedChanged = (graphic) => {
        if (feature) {
            setExistingGeometry(graphic);
            let newFeature = Object.assign({}, feature);
            newFeature.geometry = graphic;
            newFeature.isTreatment = isTreatment;
            newFeature.isPostFire = true;
            newFeature.joinFeature = newFeature.joinFeature
                ? newFeature.joinFeature
                : FieldNames.joinFeatureValues.point;
            setModifiedPerimeter(newFeature);
        }
    };

    const checkGeometryPrompt = (featureType) => {
        const unsavedFields = areUnsavedFieldsOnFeature();
        const fundingExists = areFundingLines(feature?.attributes);
        if (unsavedFields || fundingExists) {
            setAreUnsavedFields(unsavedFields);
            setFeatureHasFunding(fundingExists);
            setTempFeatureType(featureType);
            setShowGeometryConfirmPrompt(true);
        } else {
            setGeometryFieldState(featureType);
        }
    };

    const onConfirmGeometryPromptCancel = () => {
        setShowGeometryConfirmPrompt(false);
    };

    const onConfirmGeometryPromptConfirm = () => {
        if (tempFeatureType) {
            setGeometryFieldState(tempFeatureType);
            setShowGeometryConfirmPrompt(false);
        }
    };

    const areFundingLines = (currentFeature) => {
        const fundingExists =
            currentFeature &&
            currentFeature[FieldNames.featureFields.estimatedTotalCost] &&
            currentFeature[FieldNames.featureFields.estimatedTotalCost] > 0;
        return fundingExists ?? false;
    };

    const areUnsavedFieldsOnFeature = (currentFeature, isPoint) => {
        let unsavedFields = {};
        if (isActuals) {
            unsavedFields = isTreatment
                ? actualTreatmentValidationUnsavedValues
                : actualActivityValidationUnsavedValues;
        } else {
            unsavedFields = isTreatment
                ? treatmentValidationUnsavedValues
                : activityValidationUnsavedValues;
        }

        for (var prop in unsavedFields) {
            if (unsavedFields[prop] === true) {
                return true;
            }
        }

        return false;
    };

    const getViewFab = (geometryField) => {
        let result;
        let isPoint = false;
        let isLine = false;
        if (feature?.attributes) {
            if (
                feature.attributes[FieldNames.featureFields.isPoint] === 'Yes' ||
                feature.attributes[FieldNames.featureFields.joinFeature] ===
                    FieldNames.joinFeatureValues.point
            ) {
                isPoint = true;
            } else {
                if (
                    feature.attributes[FieldNames.featureFields.joinFeature] ===
                    FieldNames.joinFeatureValues.line
                ) {
                    isLine = true;
                }
            }
        }
        switch (geometryField) {
            case null:
                if (isPoint) {
                    result = (
                        <div
                            className='edit-geometry-container edit-geometry-selector'
                            id='EditGeometryToolbarAnchorEl'
                        >
                            <Fab
                                variant='extended'
                                size='medium'
                                color='primary'
                                aria-label='Edit'
                                onClick={() => {
                                    checkGeometryPrompt('POINT');
                                }}
                            >
                                <GpsFixedIcon sx={{ mr: 1 }} />
                                Change Point
                            </Fab>
                        </div>
                    );
                } else if (isLine) {
                    result = (
                        <div
                            className='edit-geometry-container edit-geometry-selector'
                            id='EditGeometryToolbarAnchorEl'
                        >
                            <Fab
                                variant='extended'
                                size='medium'
                                color='primary'
                                aria-label='Edit'
                                onClick={() => {
                                    checkGeometryPrompt('LINE');
                                }}
                            >
                                <PolylineIcon sx={{ mr: 1 }} />
                                Edit Line
                            </Fab>
                        </div>
                    );
                } else {
                    result = (
                        <div
                            className='edit-geometry-container edit-geometry-selector'
                            id='EditGeometryToolbarAnchorEl'
                        >
                            <Fab
                                variant='extended'
                                size='medium'
                                color='primary'
                                aria-label='Edit'
                                onClick={() => {
                                    checkGeometryPrompt('PERIMETER');
                                }}
                            >
                                <PolygonIcon sx={{ mr: 1 }} /> Edit Perimeter
                            </Fab>
                        </div>
                    );
                }

                break;
            case 'PERIMETER':
                result = (
                    <div
                        className='edit-geometry-container edit-geometry-selector'
                        id='EditGeometryToolbarAnchorEl'
                    >
                        <EditPerimeterToolbar
                            key='PERIMETER'
                            isTreatment={isTreatment}
                            isActuals={isActuals}
                            onConfirmPerimeterEdit={confirmPerimeterEdit}
                            onCancelPerimeterEdit={cancelPerimeterEdit}
                            onUploadPerimeter={onUploadPerimeter}
                            confirmDisabled={confirmDisabled}
                            onClearChangesPerimeter={onClearChangesPerimeter}
                            onReplacePerimeter={onReplacePerimeter}
                        />
                    </div>
                );
                break;
            case 'POINT':
                result = (
                    <div
                        className='edit-geometry-container edit-geometry-selector'
                        id='EditGeometryToolbarAnchorEl'
                    >
                        <EditPointToolbar
                            key='POINT'
                            onConfirmPointPerimeterEdit={confirmPointPerimeterEdit}
                            onCancelPointPerimeterEdit={cancelPointPerimeterEdit}
                            onConfirmPointPostFirePerimeterEdit={confirmPointPostFirePerimeterEdit}
                            isPostFire={isPostFire}
                        />
                    </div>
                );
                break;
            case 'LINE':
                result = (
                    <div
                        className='edit-geometry-container edit-geometry-selector'
                        id='EditGeometryToolbarAnchorEl'
                    >
                        <EditLineToolbar
                            key='LINE'
                            onCancelLinePerimeterEdit={cancelLinePerimeterEdit}
                            onConfirmLinePostFirePerimeterEdit={confirmLinePostFirePerimeterEdit}
                        />
                    </div>
                );
                break;
            default:
        }
        return result;
    };

    //Doing it this way to properly position where the item shows up on the page
    return ReactDOM.createPortal(
        <>
            <div>
                <InFormAlert
                    openFlag={showGeometryConfirmPrompt}
                    closeMethod={onConfirmGeometryPromptCancel}
                    buttonMethod={onConfirmGeometryPromptConfirm}
                    titleText={'Perimeter Warning'}
                    contentText={geometryPopupWarningText ?? 'Error'}
                />
            </div>
            <div className='edit-geometry-toolbar'>{getViewFab(geometryField)}</div>
        </>,
        document.getElementById('editPerimeterAnchorEl')
    );
}
